import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, firestore } from '../firebase';
import HomePage from './HomePage';
import { resolveDashboardEventId } from '../utils/activeEvent';
import { getPublicSiteMarketingConfig } from '../utils/publicSiteConfig';
import { getEventEditionYear, isEventLivePublic } from '../utils/eventRouting';

function PublicDashboardEntry() {
  const [searchParams] = useSearchParams();
  const [user, authLoading] = useAuthState(auth);
  const [decision, setDecision] = useState('loading'); // loading | home | redirect
  const [forcedEventId, setForcedEventId] = useState('');
  const [year, setYear] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const eventIdParam = searchParams.get('event');
      const yearParam = searchParams.get('year');

      let eventId;
      try {
        eventId = await resolveDashboardEventId({ eventIdParam, editionYearParam: yearParam });
      } catch {
        eventId = '';
      }
      if (!eventId) {
        const { marketingSiteBaseUrl } = await getPublicSiteMarketingConfig();
        if (cancelled) return;
        window.location.replace(marketingSiteBaseUrl);
        return;
      }

      let eventSnap;
      try {
        eventSnap = await getDoc(doc(firestore, 'events', eventId));
      } catch {
        eventSnap = null;
      }
      if (!eventSnap?.exists()) {
        const { marketingSiteBaseUrl } = await getPublicSiteMarketingConfig();
        if (cancelled) return;
        window.location.replace(marketingSiteBaseUrl);
        return;
      }

      const eventData = { id: eventSnap.id, ...eventSnap.data() };
      const editionYear = getEventEditionYear(eventData);

      const allowPublic = isEventLivePublic(eventData);

      if (allowPublic) {
        if (cancelled) return;
        setForcedEventId(eventData.id);
        setYear(editionYear);
        setDecision('home');
        return;
      }

      if (authLoading) {
        return;
      }

      if (!user) {
        const { marketingSiteBaseUrl } = await getPublicSiteMarketingConfig();
        if (cancelled) return;
        window.location.replace(marketingSiteBaseUrl);
        return;
      }

      let isAdmin = false;
      try {
        const tr = await user.getIdTokenResult(true);
        if (tr.claims.admin === true) {
          isAdmin = true;
        }
      } catch {
        isAdmin = false;
      }
      if (!isAdmin) {
        try {
          const adminSnap = await getDoc(doc(firestore, 'admins', user.uid));
          isAdmin = adminSnap.exists();
        } catch {
          isAdmin = false;
        }
      }

      if (cancelled) return;

      if (isAdmin) {
        setForcedEventId(eventData.id);
        setYear(editionYear);
        setDecision('home');
        return;
      }

      const { marketingSiteBaseUrl } = await getPublicSiteMarketingConfig();
      if (cancelled) return;
      window.location.replace(marketingSiteBaseUrl);
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [searchParams, user, authLoading]);

  if (decision === 'loading' || !forcedEventId) {
    return (
      <div style={{ padding: '2rem', fontFamily: 'system-ui, sans-serif', textAlign: 'center' }}>
        <p>Loading…</p>
      </div>
    );
  }

  return <HomePage forcedEventId={forcedEventId} year={year} />;
}

export default PublicDashboardEntry;
